import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Form, Select, DatePicker, Input, Space, 
  Typography, Tabs, Row, Col, Tag, Badge, Divider, message, 
  Tooltip, List, Avatar, Timeline, Drawer, Alert, Modal, Empty
} from 'antd';
import { 
  UserAddOutlined, ClockCircleOutlined, CalendarOutlined,
  CheckCircleOutlined, SyncOutlined, CloseCircleOutlined,
  InfoCircleOutlined, HistoryOutlined, TeamOutlined,
  FileDoneOutlined, SendOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { getExercises, assignExercise, getAssignments, getAssignmentDetails } from '../../api/exercises';
import { getTeacherStudents } from '../../api/teacherStudentRelations';
import '../../styles/AssignExercise.css';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;

const AssignExercise = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  
  // 初始化状态
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [historyDetails, setHistoryDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [filterGrade, setFilterGrade] = useState('all');
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirming, setConfirming] = useState(false);
  
  // 获取来自练习列表页面的传递参数
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const exerciseId = searchParams.get('exerciseId');
    if (exerciseId) {
      setActiveTab('1'); // 切换到分配标签页
    }
  }, [location]);
  
  // 获取练习数据
  useEffect(() => {
    setLoading(true);
    getExercises()
      .then(response => {
        setExercises(response.exercises);
        
        // 如果URL中包含exerciseId参数，预选择该练习
        const searchParams = new URLSearchParams(location.search);
        const exerciseId = searchParams.get('exerciseId');
        if (exerciseId) {
          const exercise = response.exercises.find(ex => ex.id === parseInt(exerciseId));
          if (exercise) {
            setSelectedExercise(exercise);
            form.setFieldsValue({ exercise: exercise.id });
          }
        }
        
        setLoading(false);
      })
      .catch(error => {
        console.error('获取练习列表失败:', error);
        message.error('获取练习列表失败，请稍后重试');
        setLoading(false);
      });
  }, [location, form]);
  
  // 获取学生数据
  useEffect(() => {
    getTeacherStudents()
      .then(data => {
        setStudents(data);
      })
      .catch(error => {
        console.error('获取学生列表失败:', error);
        message.error('获取学生列表失败，请稍后重试');
      });
  }, []);

  // 获取历史分配数据
  useEffect(() => {
    setLoading(true);
    getAssignments()
      .then(assignments => {
        setRecentAssignments(assignments || []);
        setLoading(false);
      })
      .catch(error => {
        console.error('获取分配历史失败:', error);
        message.error('获取分配历史失败，请稍后重试');
        setLoading(false);
      });
  }, [activeTab]);
  
  // 切换标签页
  const handleTabChange = (key) => {
    setActiveTab(key);
  };
  
  // 选择练习
  const handleExerciseChange = (exerciseId) => {
    const exercise = exercises.find(ex => ex.id === exerciseId);
    setSelectedExercise(exercise);
    
    // 如果选择的练习没有启用，显示警告
    if (exercise && !exercise.is_active) {
      message.warning('所选练习当前处于停用状态，学生将无法查看');
    }
  };
  
  // 选择学生
  const handleStudentSelectChange = (selectedIds) => {
    setSelectedStudents(selectedIds);
  };
  
  // 学生表格选择配置
  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys) => {
      setSelectedRows(selectedRowKeys);
      setSelectedStudents(selectedRowKeys);
      
      // 更新表单值
      form.setFieldValue('students', selectedRowKeys);
    }
  };
  
  // 筛选学生年级
  const handleGradeFilterChange = (value) => {
    setFilterGrade(value);
  };
  
  // 获取筛选后的学生列表
  const getFilteredStudents = () => {
    if (filterGrade === 'all') {
      return students;
    }
    return students.filter(student => student.grade_level === filterGrade);
  };
  
  // 获取学生年级选项
  const getGradeOptions = () => {
    const gradeSet = new Set(students.map(student => student.grade_level));
    return [...gradeSet].map(grade => ({
      value: grade,
      label: grade
    }));
  };
  
  // 提交分配
  const handleAssign = (values) => {
    if (!selectedExercise) {
      message.error('请选择要分配的练习');
      return;
    }
    
    if (selectedStudents.length === 0) {
      message.error('请至少选择一名学生');
      return;
    }
    
    setConfirming(true);
  };
  
  // 确认分配
  const confirmAssign = () => {
    const values = form.getFieldsValue();
    
    setLoading(true);
    const timeRange = values.timeRange;
    const assignmentData = {
      studentIds: selectedStudents,
      startTime: timeRange[0].format('YYYY-MM-DD HH:mm:ss'),
      endTime: timeRange[1].format('YYYY-MM-DD HH:mm:ss'),
      note: values.note || ''
    };
    
    assignExercise(selectedExercise.id, assignmentData)
      .then(result => {
        message.success(`成功将练习"${selectedExercise.title}"分配给${selectedStudents.length}名学生！`);
        // 重置表单
        form.resetFields();
        setSelectedExercise(null);
        setSelectedStudents([]);
        setSelectedRows([]);
        setConfirming(false);
        
        // 跳转到历史记录标签页
        setActiveTab('2');
        
        // 重新获取分配历史
        setLoading(true);
        getAssignments()
          .then(assignments => {
            setRecentAssignments(assignments || []);
            setLoading(false);
          })
          .catch(error => {
            console.error('获取分配历史失败:', error);
            setLoading(false);
          });
      })
      .catch(error => {
        console.error('分配练习失败:', error);
        message.error('分配练习失败，请稍后重试');
        setConfirming(false);
        setLoading(false);
      });
  };
  
  // 查看历史分配详情
  const viewAssignmentDetails = (assignment) => {
    setLoading(true);
    getAssignmentDetails(assignment.id)
      .then(details => {
        setHistoryDetails(details);
        setShowHistoryDrawer(true);
        setLoading(false);
      })
      .catch(error => {
        console.error('获取分配详情失败:', error);
        message.error('获取分配详情失败，请稍后重试');
        setLoading(false);
      });
  };
  
  // 状态标签渲染函数
  const renderStatusTag = (status) => {
    switch (status) {
      case 'pending':
        return <Badge status="warning" text="待开始" />;
      case 'in_progress':
        return <Badge status="processing" text="进行中" />;
      case 'completed':
        return <Badge status="success" text="已完成" />;
      case 'overdue':
        return <Badge status="error" text="已逾期" />;
      default:
        return <Badge status="default" text="未知状态" />;
    }
  };
  
  // 学生列表列定义
  const studentColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <div>
            <Text type="secondary">{record.username}</Text>
          </div>
        </div>
      )
    },
    {
      title: '年级',
      dataIndex: 'grade_level',
      key: 'grade_level',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email'
    }
  ];
  
  // 历史记录列定义
  const historyColumns = [
    {
      title: '练习名称',
      dataIndex: 'exercise_title',
      key: 'exercise_title',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: '学生数量',
      dataIndex: 'student_count',
      key: 'student_count'
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => renderStatusTag(status)
    },
    {
      title: '完成率',
      dataIndex: 'completion_rate',
      key: 'completion_rate',
      render: (rate) => {
        const percent = Math.round(rate * 100);
        let color = 'green';
        if (percent < 50) color = 'red';
        else if (percent < 80) color = 'orange';
        return <Tag color={color}>{percent}%</Tag>;
      }
    },
    {
      title: '分配时间',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            onClick={() => viewAssignmentDetails(record)}
          >
            查看详情
          </Button>
        </Space>
      )
    }
  ];
  
  return (
    <div className="assign-exercise-container">
      <Card>
        <Title level={3}>练习分配</Title>
        
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane 
            tab={<span><UserAddOutlined />分配练习</span>} 
            key="1"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAssign}
            >
              {/* 选择练习部分 */}
              <Card 
                title="第一步：选择练习" 
                className="step-card"
                extra={
                  <Button 
                    type="link" 
                    onClick={() => navigate('/teacher/exercises')}
                  >
                    管理练习
                  </Button>
                }
              >
                <Form.Item
                  name="exercise"
                  rules={[{ required: true, message: '请选择练习' }]}
                >
                  <Select
                    placeholder="请选择要分配的练习"
                    onChange={handleExerciseChange}
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    showSearch
                  >
                    {exercises.map(exercise => (
                      <Option 
                        key={exercise.id} 
                        value={exercise.id}
                        disabled={!exercise.is_active}
                      >
                        {exercise.title}
                        {!exercise.is_active && ' (已停用)'}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                
                {selectedExercise && (
                  <Card size="small" className="selected-exercise-card">
                    <Row gutter={16}>
                      <Col span={18}>
                        <Title level={5}>{selectedExercise.title}</Title>
                        <Paragraph ellipsis={{ rows: 2 }}>
                          {selectedExercise.description}
                        </Paragraph>
                        <div>
                          <Tag color="blue">{selectedExercise.category_name}</Tag>
                          <Tag color="orange">
                            {
                              selectedExercise.difficulty === 'easy' ? '简单' :
                              selectedExercise.difficulty === 'medium' ? '中等' :
                              selectedExercise.difficulty === 'hard' ? '困难' : '高级'
                            }
                          </Tag>
                          <Tag color="purple">{selectedExercise.duration}分钟</Tag>
                          <Tag color="cyan">题目数：{selectedExercise.question_count}</Tag>
                        </div>
                      </Col>
                      <Col span={6} className="exercise-stats">
                        <Statistic 
                          title="总分" 
                          value={selectedExercise.total_points} 
                          suffix="分"
                        />
                        <Statistic 
                          title="及格分" 
                          value={selectedExercise.passing_points} 
                          suffix="分"
                        />
                      </Col>
                    </Row>
                  </Card>
                )}
              </Card>
              
              {/* 选择学生部分 */}
              <Card 
                title="第二步：选择学生" 
                className="step-card"
                extra={
                  <Space>
                    <Text>年级筛选：</Text>
                    <Select
                      defaultValue="all"
                      style={{ width: 120 }}
                      onChange={handleGradeFilterChange}
                    >
                      <Option value="all">全部年级</Option>
                      {getGradeOptions().map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Space>
                }
              >
                <Form.Item
                  name="students"
                  rules={[{ required: true, message: '请至少选择一名学生' }]}
                >
                  <Table
                    rowSelection={rowSelection}
                    columns={studentColumns}
                    dataSource={getFilteredStudents()}
                    rowKey="id"
                    pagination={{
                      defaultPageSize: 5,
                      showSizeChanger: true,
                      pageSizeOptions: ['5', '10', '20']
                    }}
                    size="middle"
                  />
                </Form.Item>
                
                <Alert
                  message={`已选择 ${selectedStudents.length} 名学生`}
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              </Card>
              
              {/* 设置时间部分 */}
              <Card title="第三步：设置练习时间" className="step-card">
                <Form.Item
                  name="timeRange"
                  label="练习开始和结束时间"
                  rules={[{ required: true, message: '请设置练习的开始和结束时间' }]}
                >
                  <RangePicker
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    placeholder={['开始时间', '结束时间']}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                
                <Form.Item
                  name="note"
                  label="备注（可选）"
                >
                  <Input.TextArea
                    placeholder="可以添加一些练习说明、要求或注意事项"
                    rows={4}
                  />
                </Form.Item>
                
                <Divider />
                
                <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SendOutlined />}
                    size="large"
                    disabled={!selectedExercise || selectedStudents.length === 0}
                  >
                    分配练习
                  </Button>
                </Form.Item>
              </Card>
            </Form>
          </TabPane>
          
          <TabPane 
            tab={<span><HistoryOutlined />历史记录</span>} 
            key="2"
          >
            <Table
              columns={historyColumns}
              dataSource={recentAssignments}
              rowKey="id"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true
              }}
            />
          </TabPane>
        </Tabs>
      </Card>
      
      {/* 分配确认模态框 */}
      <Modal
        title="确认分配练习"
        open={confirming}
        onOk={confirmAssign}
        onCancel={() => setConfirming(false)}
        okText="确认分配"
        cancelText="取消"
        confirmLoading={loading}
      >
        <div>
          {selectedExercise && (
            <>
              <Paragraph>
                您即将将练习 <Text strong>{selectedExercise.title}</Text> 分配给 <Text strong>{selectedStudents.length}</Text> 名学生。
              </Paragraph>
              <Paragraph>
                请确认以下信息无误：
              </Paragraph>
              <ul>
                <li>总分：{selectedExercise.total_points}分</li>
                <li>难度：{
                  selectedExercise.difficulty === 'easy' ? '简单' :
                  selectedExercise.difficulty === 'medium' ? '中等' :
                  selectedExercise.difficulty === 'hard' ? '困难' : '高级'
                }</li>
                <li>题目数：{selectedExercise.question_count}</li>
                <li>练习时长：{selectedExercise.duration}分钟</li>
                <li>时间范围：{form.getFieldValue('timeRange') ? 
                    `${form.getFieldValue('timeRange')[0].format('YYYY-MM-DD HH:mm')} 至 ${form.getFieldValue('timeRange')[1].format('YYYY-MM-DD HH:mm')}` 
                    : '未设置'}</li>
              </ul>
              
              {!selectedExercise.is_active && (
                <Alert
                  message="警告：所选练习当前处于停用状态"
                  description="此练习已被停用，分配后学生将无法查看。请先在练习管理中启用该练习。"
                  type="warning"
                  showIcon
                  style={{ marginTop: 16, marginBottom: 16 }}
                />
              )}
            </>
          )}
        </div>
      </Modal>
      
      {/* 历史详情抽屉 */}
      <Drawer
        title="分配详情"
        placement="right"
        onClose={() => setShowHistoryDrawer(false)}
        open={showHistoryDrawer}
        width={500}
      >
        {historyDetails && (
          <div>
            <Paragraph>
              <Title level={4}>{historyDetails.exercise_title}</Title>
              <div className="detail-item">
                <CalendarOutlined /> 开始时间：{dayjs(historyDetails.start_time).format('YYYY-MM-DD HH:mm')}
              </div>
              <div className="detail-item">
                <CalendarOutlined /> 结束时间：{dayjs(historyDetails.end_time).format('YYYY-MM-DD HH:mm')}
              </div>
              <div className="detail-item">
                <TeamOutlined /> 分配学生数：{historyDetails.student_count || 0}
              </div>
              <div className="detail-item">
                <ClockCircleOutlined /> 分配时间：{dayjs(historyDetails.assigned_at || historyDetails.created_at).format('YYYY-MM-DD HH:mm')}
              </div>
              <div className="detail-item">
                <FileDoneOutlined /> 完成情况：{Math.round((historyDetails.completion_rate || 0) * 100)}%
              </div>
              <div className="detail-item">
                <InfoCircleOutlined /> 状态：{renderStatusTag(historyDetails.status)}
              </div>
            </Paragraph>
            
            <Divider />
            
            <Title level={5}>学生完成情况</Title>
            {historyDetails.students ? (
              <List
                size="small"
                bordered
                loading={loading}
                dataSource={historyDetails.students || []}
                renderItem={student => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar>{student.student_name?.charAt(0) || 'S'}</Avatar>}
                      title={student.student_name}
                      description={
                        student.status === 'completed' 
                          ? `得分: ${student.score || 0}分 - 提交时间: ${student.submission_time ? dayjs(student.submission_time).format('HH:mm') : '未提交'}`
                          : student.status === 'in_progress' ? '进行中' : '未开始'
                      }
                    />
                    {student.status === 'completed' 
                      ? <CheckCircleOutlined style={{ color: 'green' }} /> 
                      : student.status === 'in_progress'
                        ? <SyncOutlined spin />
                        : <ClockCircleOutlined style={{ color: 'orange' }} />}
                  </List.Item>
                )}
                locale={{ emptyText: '暂无学生完成情况数据' }}
              />
            ) : (
              <Empty description="暂无学生完成详情数据" />
            )}
            
            <Divider />
            
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Space>
                <Button onClick={() => setShowHistoryDrawer(false)}>关闭</Button>
                <Button type="primary">导出成绩</Button>
              </Space>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

// 添加缺失的Statistic组件
const Statistic = ({ title, value, suffix }) => (
  <div className="statistic">
    <div className="statistic-title">{title}</div>
    <div className="statistic-value">{value}{suffix}</div>
  </div>
);

export default AssignExercise;